/**
 * InvoiceOutreachTab.tsx — Billing Phase 2
 * Espejo exacto de EstimateOutreachTab.tsx. Feed ASC de drafts (invoice_review /
 * payment_reminder / reply) + replies capturadas. Display-only para replies.
 */
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../ui/DetailSection.js';
import { EmptyState } from '../ui/EmptyState.js';
import { LoadingState } from '../ui/LoadingState.js';
import {
  useInvoiceDrafts,
  useInvoiceReplies,
  type InvoiceOutreachDraft,
  type InvoiceOutreachReply,
} from '../../hooks/useInvoiceOutreach.js';
import { InvoiceDraftCardConnected } from './InvoiceDraftCardConnected.js';
import { InvoiceReplyCardConnected } from './InvoiceReplyCardConnected.js';
import type { Invoice } from '../../hooks/useBilling.js';

type FeedItem =
  | { kind: 'draft'; ts: number; item: InvoiceOutreachDraft }
  | { kind: 'reply'; ts: number; item: InvoiceOutreachReply };

interface InvoiceOutreachTabProps {
  invoice: Invoice;
}

export function InvoiceOutreachTab({ invoice }: InvoiceOutreachTabProps) {
  const { t } = useTranslation();
  const invoiceId = invoice.id;

  const { data: draftsData, isLoading: draftsLoading } = useInvoiceDrafts(invoiceId);
  const { data: repliesData, isLoading: repliesLoading } = useInvoiceReplies(invoiceId);

  const isLoading = draftsLoading || repliesLoading;

  const feed: FeedItem[] = [
    ...(draftsData?.drafts ?? []).map(d => ({ kind: 'draft' as const, ts: d.sentAt ?? d.createdAt, item: d })),
    ...(repliesData?.replies ?? []).map(r => ({ kind: 'reply' as const, ts: r.receivedAt, item: r })),
  ].sort((a, b) => a.ts - b.ts);

  const isEmpty = !isLoading && feed.length === 0;

  return (
    <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>
      <DetailSection title={t('billing.outreach.title')} divider={false}>
        {isLoading && <LoadingState />}
        {isEmpty && <EmptyState title={t('billing.outreach.empty')} />}
        {feed.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {feed.map(item =>
              item.kind === 'draft'
                ? <InvoiceDraftCardConnected key={`d-${item.item.id}`} draft={item.item} invoiceId={invoiceId} />
                : <InvoiceReplyCardConnected key={`r-${item.item.id}`} reply={item.item} invoiceId={invoiceId} />,
            )}
          </div>
        )}
      </DetailSection>
    </div>
  );
}
